import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { employee } from '../_actions/app-flow-state.actions';

const VERIFIER_KEY = 'employeeSsoVerifier';
const STATE_KEY = 'employeeSsoState';
const CLIENT_ID_KEY = 'employeeSsoClientId';
const TENANT_ID_KEY = 'employeeSsoTenantId';
const SSO_SCOPE = 'openid profile email https://graph.microsoft.com/GroupMember.Read.All';

@Component({
  selector: 'app-employee-sso',
  standalone: false,
  template: `
    <div class="employee-sso">
      <p role="status">{{ message }}</p>
      <button *ngIf="signInFailed" type="button" class="btn primary-btn employee-sso__retry" (click)="retrySignIn()">
        Sign in again
      </button>
    </div>
  `,
  styles: [`
    .employee-sso { padding: 2rem; text-align: center; }
    .employee-sso__retry { margin-top: 1rem; }
  `],
})
export class EmployeeSsoComponent implements OnInit {
  message = 'Signing in...';
  signInFailed = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    if (error || errorDescription) {
      console.warn('[employee-sso] Microsoft authorize redirect returned an error', {
        error,
        error_description: errorDescription,
        error_uri: params.get('error_uri'),
        error_subcode: params.get('error_subcode'),
      });
      this.fail(error === 'access_denied' ? 'Sign-in was cancelled.' : 'Sign-in failed. Please try again.');
      return;
    }
    const code = params.get('code');
    if (code) {
      this.completeLogin(code, params.get('state'));
      return;
    }
    this.startLogin();
  }

  private startLogin(): void {
    this.http.get<{ success: boolean; clientId: string; tenantId: string }>(
      `${environment.apiUrl}/v1/api/employee-sso/config`
    ).subscribe({
      next: async (config) => {
        const verifier = randomString(64);
        const state = randomString(32);
        sessionStorage.setItem(VERIFIER_KEY, verifier);
        sessionStorage.setItem(STATE_KEY, state);
        sessionStorage.setItem(CLIENT_ID_KEY, config.clientId);
        sessionStorage.setItem(TENANT_ID_KEY, config.tenantId);
        const challenge = await sha256Base64Url(verifier);
        const redirectUri = `${window.location.origin}/sgiemployee`;
        const authorize = new URL(`https://login.microsoftonline.com/${config.tenantId}/oauth2/v2.0/authorize`);
        authorize.searchParams.set('client_id', config.clientId);
        authorize.searchParams.set('response_type', 'code');
        authorize.searchParams.set('redirect_uri', redirectUri);
        authorize.searchParams.set('response_mode', 'query');
        authorize.searchParams.set('scope', SSO_SCOPE);
        authorize.searchParams.set('code_challenge', challenge);
        authorize.searchParams.set('code_challenge_method', 'S256');
        authorize.searchParams.set('state', state);
        console.warn('[employee-sso] Microsoft authorize request', {
          url: `${authorize.origin}${authorize.pathname}`,
          tenantId: config.tenantId,
          client_id: config.clientId,
          response_type: 'code',
          redirect_uri: redirectUri,
          response_mode: 'query',
          scope: authorize.searchParams.get('scope'),
          code_challenge_method: 'S256',
        });
        window.location.assign(authorize.toString());
      },
      error: (err) => {
        console.warn('[employee-sso] config request failed', {
          url: `${environment.apiUrl}/v1/api/employee-sso/config`,
          status: err?.status,
          body: err?.error,
        });
        this.fail('Employee sign-in is not available.');
      },
    });
  }

  retrySignIn(): void {
    this.signInFailed = false;
    this.message = 'Signing in...';
    window.history.replaceState({}, '', '/sgiemployee');
    this.startLogin();
  }

  private completeLogin(code: string, state: string | null): void {
    const expectedState = sessionStorage.getItem(STATE_KEY);
    const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
    const clientId = sessionStorage.getItem(CLIENT_ID_KEY);
    const tenantId = sessionStorage.getItem(TENANT_ID_KEY);
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(CLIENT_ID_KEY);
    sessionStorage.removeItem(TENANT_ID_KEY);
    if (!codeVerifier || !clientId || !tenantId || !state || state !== expectedState) {
      this.fail('Sign-in could not be verified. Please try again.');
      return;
    }

    const redirectUri = `${window.location.origin}/sgiemployee`;
    const tokenUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`;
    const body = new URLSearchParams({
      client_id: clientId,
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
      scope: SSO_SCOPE,
    });
    console.warn('[employee-sso] Microsoft token request', {
      url: tokenUrl,
      client_id: clientId,
      redirect_uri: redirectUri,
      grant_type: 'authorization_code',
    });
    void this.redeemCode(tokenUrl, body);
  }

  private async redeemCode(tokenUrl: string, body: URLSearchParams): Promise<void> {
    let accessToken = '';
    try {
      const tokenResponse = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const tokenBody = await tokenResponse.json();
      accessToken = tokenBody?.access_token || '';
      if (!tokenResponse.ok || !accessToken) {
        console.warn('[employee-sso] Microsoft token response', {
          status: tokenResponse.status,
          error: tokenBody?.error,
          error_description: tokenBody?.error_description,
        });
        this.fail('Sign-in failed. Please try again.');
        return;
      }
    } catch (error) {
      console.warn('[employee-sso] Microsoft token request failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      this.fail('Sign-in failed. Please try again.');
      return;
    }

    const callbackUrl = `${environment.apiUrl}/v1/api/employee-sso/callback`;
    console.warn('[employee-sso] callback request', {
      url: callbackUrl,
      hasAccessToken: true,
    });
    try {
      const callbackResponse = await fetch(callbackUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken }),
      });
      const callbackBody = await callbackResponse.json().catch(() => ({}));
      console.warn('[employee-sso] callback response', {
        status: callbackResponse.status,
        body: callbackBody,
      });
      if (!callbackResponse.ok || !callbackBody?.success) {
        this.fail(callbackBody?.errorMessage || 'This account is not allowed to use employee rates.');
        return;
      }
      this.store.dispatch(employee());
      this.router.navigate(['/vehicle-service-protection']);
    } catch (error) {
      console.warn('[employee-sso] callback request failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      this.fail('This account is not allowed to use employee rates.');
    }
  }

  private fail(message: string): void {
    this.message = message;
    this.signInFailed = true;
  }
}

function randomString(size: number): string {
  const bytes = new Uint8Array(size);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return base64Url(new Uint8Array(digest));
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
